import { normalizeCompanyName } from './normalise.js';
export function agencySourceValid(source) {
    if (!source)
        return false;
    try {
        const url = new URL(source);
        return url.protocol === 'https:' && !!url.hostname.includes('.') && url.username === '' && url.password === '';
    }
    catch {
        return false;
    }
}
export function agencyResearchReady(profile) {
    return !!profile && agencySourceValid(profile.sourceUrl) && !!profile.evidence?.trim() && !!profile.commercialAngle?.trim();
}
export function agencyOutreachEligible(profile) {
    return agencyResearchReady(profile) && !!profile && !['research', 'not-fit'].includes(profile.relationship);
}
export function agencyPositioningSuggestion(profile) {
    // A public portfolio is not proof of a resource shortage; use explicitly reviewed evidence and a human choice.
    if (!profile || !agencyResearchReady(profile))
        return { positioning: 'both', reason: 'Research the agency and its actual projects before choosing a pitch. Do not assume a capability gap or capacity shortage.' };
    const label = profile.positioning;
    return { positioning: label, reason: label === 'external-resource' ? 'Position as flexible specialist support. Do not claim the agency is understaffed.' : label === 'delivery-partner' ? 'Offer ownership of a clearly scoped specialist creative deliverable, without implying existing suppliers are inadequate.' : 'Present both flexible specialist support and ownership of defined creative work. Tailor the emphasis to the person and brief.' };
}
export function agencyRelationshipMetrics(company, contacts, sequences) {
    const key = normalizeCompanyName(company.name);
    const people = contacts.filter(c => !c.archived && (c.companyId === company.id || normalizeCompanyName(c.companyName) === key));
    const ids = new Set(people.map(c => c.id));
    const sent = sequences.filter(s => !s.archived && s.stage === 'sent' && (ids.has(s.contactId) || s.companyId === company.id));
    const latest = sent.map(s => Math.max(Date.parse(s.firstSentAt || '') || 0, Date.parse(s.followUpSentAt || '') || 0)).reduce((a, b) => Math.max(a, b), 0);
    const replies = sent.filter(s => s.replyClassification !== 'automatic' && ['reply-positive', 'reply-neutral', 'not-interested', 'not-now', 'meeting-booked', 'brief-received', 'project-won'].includes(s.outcome));
    const meetings = sent.filter(s => ['meeting-booked', 'brief-received', 'project-won'].includes(s.outcome));
    const wins = sent.filter(s => s.outcome === 'project-won');
    const pending = sequences.filter(s => !s.archived && s.stage !== 'sent' && (ids.has(s.contactId) || s.companyId === company.id)).length;
    return { people, contacts: people.length, sent: sent.length, replies: replies.length, meetings: meetings.length, wins: wins.length, pending, lastSentAt: latest ? new Date(latest).toISOString() : undefined };
}
export function agencyAccountPriority(company, contacts, sequences) {
    const p = company.agencyPartnership;
    const metrics = agencyRelationshipMetrics(company, contacts, sequences);
    if (!p)
        return { label: 'Unreviewed', reasons: ['Not researched or verified as an agency'], sort: 0 };
    if (p.relationship === 'not-fit')
        return { label: 'Closed', reasons: ['Marked not a fit'], sort: -1 };
    const reasons = [];
    let score = 0;
    if (agencyResearchReady(p)) {
        score += 4;
        reasons.push('Reviewed source, services and commercial angle');
    }
    else
        reasons.push('Review a source and specific reason to partner');
    if (p.serviceFocus.length) {
        score += 2;
        reasons.push(`${p.serviceFocus.length} matched BAILEY service area${p.serviceFocus.length === 1 ? '' : 's'}`);
    }
    if (metrics.people.length) {
        score += 2;
        reasons.push(`${metrics.people.length} CRM contact${metrics.people.length === 1 ? '' : 's'}`);
    }
    if (metrics.replies) {
        score += 4;
        reasons.push(`${metrics.replies} genuine reply${metrics.replies === 1 ? '' : 's'}`);
    }
    if (metrics.wins) {
        score += 8;
        reasons.push(`${metrics.wins} project${metrics.wins === 1 ? '' : 's'} won`);
    }
    if (p.relationship === 'conversation' || p.relationship === 'preferred' || p.relationship === 'repeat')
        score += 4;
    if (metrics.pending)
        reasons.push(`${metrics.pending} active email${metrics.pending === 1 ? '' : 's'} already in pipeline`);
    return { label: score >= 10 ? 'Active relationship' : score >= 6 ? 'Strong lead' : agencyResearchReady(p) ? 'Research complete' : 'Research needed', reasons, sort: score };
}
